"use client";

/**
 * One place's demand, for the map's SideDrawer: the index with its trend,
 * the 12-week series (thin weeks left as gaps), searches against listings
 * by room count, and the split by segment. Values only for Pro; the
 * locked shape renders the same layout with the numbers blurred.
 */
import { useEffect, useState, useRef } from "react";
import { LINK, PRO_BADGE, ROW_ON } from "@/lib/ui-classes";
import { ArrowDownRight, ArrowRight, ArrowUpRight, Loader2, Lock } from "lucide-react";
import LocalizedLink from "@/lib/localized-link";
import { useLocale, useT } from "@/lib/locale-context";
import { formatPrice } from "@/lib/api";
import { trackEvent } from "@/lib/tracking";
import type { TranslationKey } from "@/lib/i18n";
import ProCta from "@/components/result/ProCta";
import {
  demandColor, fetchDemandPlace, formatRange, placeHref, weekLabel,
  type DemandKind, type DemandPlaceDetail, type DemandPoint, type DemandSegment,
} from "@/lib/demand";

export function TrendBadge({ trend, className = "" }: { trend: number | null | undefined; className?: string }) {
  const t = useT();
  if (trend == null) return null;
  const Icon = trend > 0 ? ArrowUpRight : trend < 0 ? ArrowDownRight : ArrowRight;
  const tone = trend > 0 ? "text-violet-700 dark:text-violet-300" : trend < 0 ? "text-sky-700 dark:text-sky-300" : "text-gray-600";
  const text = trend > 0
    ? t("demand.trend.up").replace("{n}", String(trend))
    : trend < 0 ? t("demand.trend.down").replace("{n}", String(-trend)) : t("demand.trend.flat");
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold tabular-nums ${tone} ${className}`}>
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {text}
    </span>
  );
}

/** 12 bars of the index, the dashed line at 50 (the average place).
 *  One hue, the newest week darker: the step colours of the map would
 *  flip back and forth for values near a step boundary. */
export function DemandBars({ series, locked }: { series: DemandPoint[]; locked?: boolean }) {
  const { locale } = useLocale();
  const t = useT();
  const W = 300, H = 110, pad = 14;
  // A finger or a pointer on the bars names the week (owner, 2026-10-10).
  const [picked, setPicked] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const pick = (e: React.PointerEvent) => {
    const r = svgRef.current?.getBoundingClientRect(); if (!r || !series.length) return;
    const fx = ((e.clientX - r.left) / r.width) * W;
    setPicked(Math.max(0, Math.min(series.length - 1, Math.floor((fx - 2) / ((W - 4) / Math.max(series.length, 1))))));
  };
  const cur = series.length ? series[picked ?? series.length - 1] : null;
  const n = Math.max(series.length, 1);
  const bw = (W - 4) / n;
  const y = (v: number) => H - pad - (v / 100) * (H - pad - 4);
  return (
    <figure className={locked ? "select-none blur-[3px]" : ""} aria-hidden={locked || undefined}>
      {/* What the bars are (owner, 2026-10-11: "a date and a number, I
          don't know what exactly that number is"). */}
      <p className="mb-1 text-[11px] font-semibold text-gray-500">{t("demand.series.title")} · {t("demand.series.index")}</p>
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="h-auto w-full touch-none" role="img" aria-label={t("demand.series.title")} onPointerDown={pick} onPointerMove={(e) => { if (e.pointerType !== "mouse" && e.buttons === 0) return; pick(e); }} onPointerLeave={() => setPicked(null)}>
        <line x1={0} x2={W} y1={y(50)} y2={y(50)} stroke="currentColor" className="text-gray-300" strokeDasharray="3 3" />
        {series.map((p, i) => {
          const v = locked ? 30 + ((i * 37) % 45) : p.index;
          const x = 2 + i * bw + bw * 0.15;
          return v == null ? (
            <rect key={p.week} x={x} y={H - pad - 2} width={bw * 0.7} height={2} rx={1} className="fill-gray-300" />
          ) : (
            <rect key={p.week} x={x} y={y(v)} width={bw * 0.7} height={H - pad - y(v)} rx={2} fill={i === (picked ?? series.length - 1) ? "#6d28d9" : "#a78bfa"}>
              <title>{`${weekLabel(p.week, locale)}: ${v}`}</title>
            </rect>
          );
        })}
        {series.length > 0 && (
          <>
            <text x={2} y={H - 2} className="fill-gray-500 text-[9px]">{weekLabel(series[0].week, locale)}</text>
            <text x={W - 2} y={H - 2} textAnchor="end" className="fill-gray-500 text-[9px]">{weekLabel(series[series.length - 1].week, locale)}</text>
          </>
        )}
      </svg>
      {cur && !locked && (
        <figcaption className="mt-1 text-xs tabular-nums text-gray-700"><span className="text-gray-500">{weekLabel(cur.week, locale)}</span> · <span className="font-semibold text-gray-900">{cur.index ?? t("common.na")}</span> <span className="text-gray-500">/ 100</span></figcaption>
      )}
    </figure>
  );
}

const ROOMS_LABEL = (r: string, t: (k: TranslationKey) => string) =>
  t("demand.rooms.n").replace("{n}", r === "4" ? "4+" : r);

export default function DemandPlacePanel({ kind, slug, segment, onSegment }: {
  kind: DemandKind;
  slug: string;
  segment: DemandSegment;
  onSegment?: (s: DemandSegment) => void;
}) {
  const t = useT();
  const { locale } = useLocale();
  const [data, setData] = useState<DemandPlaceDetail | null | undefined>(undefined);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    setData(undefined);
    setFailed(false);
    fetchDemandPlace(kind, slug, segment)
      .then((d) => { if (live) setData(d); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [kind, slug, segment]);

  const range = (r: Parameters<typeof formatRange>[0]) => formatRange(r, t("demand.range")) ?? t("common.na");

  if (failed) return <p className="text-sm text-gray-600">{t("demand.error")}</p>;
  if (data === undefined) {
    return <p className="flex items-center gap-2 text-sm text-gray-600"><Loader2 className="h-4 w-4 animate-spin" aria-hidden />{t("demand.loading")}</p>;
  }
  if (data === null) return <p className="text-sm text-gray-600">{t("demand.thin.body")}</p>;

  const locked = data.locked;
  const L = data.latest;
  const blur = locked ? "select-none blur-[5px]" : "";
  const windowText = data.window === "4w" ? t("demand.window.4w") : t("demand.week").replace("{d}", weekLabel(data.week, locale));

  return (
    <div className="space-y-4 text-sm sm:space-y-5">
      <p className="text-xs text-gray-500">
        {t(`demand.kind.${data.kind}` as TranslationKey)}, {windowText}
      </p>

      {/* The one number that leads. */}
      {!locked && data.thin ? (
        <div className="rounded-xl border border-gray-200 p-3">
          <p className="font-semibold text-gray-900">{t("demand.thin.title")}</p>
          <p className="mt-1 text-[13px] leading-relaxed text-gray-600">{t("demand.thin.body")}</p>
        </div>
      ) : (
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="text-xs font-medium text-gray-500">{t("demand.index")}</p>
            <p className="mt-0.5 flex items-baseline gap-1.5">
              {!locked && <span className="h-3 w-3 shrink-0 self-center rounded-full" style={{ backgroundColor: demandColor(L?.index) }} aria-hidden />}
              <span className={`text-4xl font-extrabold tabular-nums tracking-tight text-gray-900 ${blur}`}>
                {locked ? "64" : L?.index ?? t("common.na")}
              </span>
              <span className="text-xs text-gray-500">{t("demand.index.of")}</span>
            </p>
            {!locked && <TrendBadge trend={L?.trend} className="mt-1" />}
            {!locked && L?.trend != null && <p className="text-[11px] text-gray-500">{t("demand.trend.caption")}</p>}
          </div>
          {locked && (
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${PRO_BADGE}`}>
              <Lock className="h-3 w-3" aria-hidden /> Pro
            </span>
          )}
        </div>
      )}

      <dl className="grid grid-cols-3 gap-px overflow-hidden rounded-xl border border-gray-200 bg-gray-200">
        {([
          ["demand.visitors", locked ? "50-99" : range(L?.sessions)],
          ["demand.active", locked ? "120" : L ? formatPrice(L.active) : t("common.na")],
          ["demand.new", locked ? "14" : L ? formatPrice(L.new) : t("common.na")],
        ] as [TranslationKey, string][]).map(([k, v]) => (
          <div key={k} className="bg-white px-3 py-2">
            <dt className="text-[11px] text-gray-500">{t(k)}</dt>
            <dd className={`mt-0.5 font-bold tabular-nums text-gray-900 ${blur}`}>{v}</dd>
          </div>
        ))}
      </dl>

      <section>
        <h3 className="text-[13px] font-semibold text-gray-900">{t("demand.series.title")}</h3>
        <div className="mt-2">
          <DemandBars series={data.series} locked={locked} />
        </div>
        {!locked && data.series.some((p) => p.thin) && (
          <p className="mt-1 text-[11px] text-gray-500">{t("demand.series.thin")}</p>
        )}
      </section>

      {(locked || data.searches_vs_supply) && (
        <section>
          <h3 className="text-[13px] font-semibold text-gray-900">{t("demand.rooms.title")}</h3>
          <p className="text-[11px] text-gray-500">{t("demand.window.4w")}, {t(`demand.segment.${data.segment}` as TranslationKey)}</p>
          <table className="mt-2 w-full text-[13px]">
            <thead>
              <tr className="text-[11px] text-gray-500">
                <th className="py-1 text-left font-medium" />
                <th className="py-1 text-right font-medium">{t("demand.rooms.searches")}</th>
                <th className="py-1 text-right font-medium">{t("demand.rooms.active")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data.searches_vs_supply ?? ["1", "2", "3", "4"].map((r) => ({ rooms: r, searches: null, active: 0 }))).map((r) => (
                <tr key={r.rooms} className="tabular-nums">
                  <td className="py-1.5 text-gray-700">{ROOMS_LABEL(r.rooms, t)}</td>
                  <td className={`py-1.5 text-right font-semibold text-gray-900 ${blur}`}>{locked ? "5-9" : range(r.searches)}</td>
                  <td className={`py-1.5 text-right text-gray-700 ${blur}`}>{locked ? "40" : formatPrice(r.active)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {data.segments.length > 1 && (
        <section>
          <h3 className="text-[13px] font-semibold text-gray-900">{t("demand.segments.title")}</h3>
          <ul className="mt-2 space-y-1">
            {data.segments.filter((s) => s.segment !== "all").map((s) => (
              <li key={s.segment}>
                <button
                  type="button"
                  disabled={!onSegment}
                  onClick={() => onSegment?.(s.segment)}
                  className={`flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left ${s.segment === data.segment ? ROW_ON : "hover:bg-gray-50"}`}
                >
                  <span className="truncate">{t(`demand.segment.${s.segment}` as TranslationKey)}</span>
                  <span className={`tabular-nums font-semibold ${blur}`}>
                    {locked ? "52" : s.thin ? t("demand.segment.thin") : s.index}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {locked ? (
        <div className="space-y-2">
          <p className="text-[13px] leading-relaxed text-gray-600">{t("demand.locked.body")}</p>
          <div onClick={() => trackEvent("demand.cta.click", { where: "drawer" })}>
            <ProCta />
          </div>
        </div>
      ) : (
        <LocalizedLink href={placeHref(data.kind, data.slug)} className={`inline-flex text-[13px] ${LINK}`}>
          {t("demand.open_page")}
        </LocalizedLink>
      )}
      <p className="text-[11px] leading-snug text-gray-500">{t("demand.privacy")}</p>
    </div>
  );
}
